import { Component, OnInit, AfterViewInit, Input, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, CommunityService, environment, EventBusService, HelperService, ProductService, UserService, WindowService, WorkspaceService } from "@mypxplat/xplat/core";
import { Router } from "@angular/router";
import { AppService, AvoService, BetaService, WebCommunityService } from "@mypxplat/xplat/web/core";
import anime from "animejs/lib/anime.es.js";
import { TranslateService } from "@ngx-translate/core";
import { filter, take, takeUntil, throttle } from "rxjs/operators";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { interval } from "rxjs";
import { AudioPlayerComponent } from "../audio-player/audio-player.component";
import { CloudNavOverlayComponent } from "../cloud-nav-overlay/cloud-nav-overlay.component";
import { UploadDetailsComponent } from "../modals/upload-details/upload-details.component";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-cloud-nav",
  templateUrl: "cloud-nav.component.html",
  standalone: false,
})
export class CloudNavComponent extends BaseComponent implements OnInit {
  @Input() logo: string = "./assets/icons/plus-icon-blue.png";
  @Input() textLogo: string;
  @ViewChild("audioPlayer") audioPlayer: AudioPlayerComponent;
  public currentLanguage: any;
  public totalUploadPercentage: any;
  public uploadingFileCount: number;
  public showNotifications: boolean = false;
  public showAudioPlayerIcon: boolean = false;
  public showAudioPlayer: boolean = false;
  public environment = environment;

  /**
   * Community appears in the nav for authed users always, and for guests only when
   * the communityPublicAccess flag is on. Keeps the guest header simple and matches
   * the CommunityPublicRouteGuard behavior — if the flag is off, guests hitting the
   * link would just bounce to login anyway.
   */
  get showCommunityLink(): boolean {
    if (this.user) return true;
    return !!(environment.features as any).communityPublicAccess;
  }
  constructor(
    userService: UserService,
    public router: Router,
    public appService: AppService,
    public betaService: BetaService,
    private _win: WindowService,
    private _translateService: TranslateService,
    public helperService: HelperService,
    public workspaceService: WorkspaceService,
    public fbAuth: AngularFireAuth,
    public communityService: WebCommunityService,
    public productService: ProductService,
    public avoService: AvoService,
    private eventBus: EventBusService
  ) {
    super(userService);
    this._translateService.onLangChange.subscribe((result) => {
      this.currentLanguage = this.helperService.languages.filter((lang) => lang.code == result.lang)[0];
    });
    this.eventBus.observe(this.eventBus.types.playAudio).subscribe((result) => {
      this.showAudioPlayerIcon = result;
    });
    this.userService.user$
      .pipe(
        filter((user) => !!user),
        take(1)
      )
      .subscribe((result) => {
        if (this.helperService.selectedLanguage) {
          this.currentLanguage = this.helperService.selectedLanguage;
        } else if (this.user.language_code) {
          this.currentLanguage = this.helperService.languages.filter((lang) => lang.code == this.user.language_code)[0];
        }
        if (!this.currentLanguage) {
          this.helperService.selectedLanguage = this.helperService.languages.filter((lang) => lang.code == "en")[0];
          this.currentLanguage = this.helperService.selectedLanguage;
          if (this.user) {
            this.userService
              .saveUser({
                id: this.user.id,
                language_code: "en",
              })
              .subscribe((result) => {});
          }
        }
      });
  }

  ngOnInit() {
    this.workspaceService.fileUploadSessionCompleted$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.uploadingFileCount = 0;
        this.totalUploadPercentage = undefined;
      }
    });

    this.workspaceService.fileUploadProgressUpdate$
      .pipe(
        takeUntil(this.destroy$),
        throttle(
          () => {
            return interval(200);
          },
          { leading: true, trailing: true }
        )
      )
      .subscribe((result) => {
        this.uploadingFileCount = Object.keys(this.workspaceService.uploadingFilesSessionMap).length;
        if (this.uploadingFileCount > 0) {
          let progresses = [];
          let total = 0;
          Object.values(this.workspaceService.uploadingFilesSessionMap).forEach((item: any) => {
            total += item.progress;
            progresses.push(item.progress);
          });
          this.totalUploadPercentage = (total / this.uploadingFileCount).toFixed(0);
        } else {
          this.uploadingFileCount = 0;
          this.totalUploadPercentage = undefined;
        }
      });
  }

  profilePhotoClicked(section = "about") {
    if (this.userService.appId === "fender") {
      const routeProfileId = (this.user as any)["fc-id"] || this.user.id;
      if (!routeProfileId) {
        this.appService.alert(this._translateService.instant("com.profile_not_available"));
        return;
      }
      this.router.navigate(["/profile", routeProfileId, section]);
    } else {
      this.router.navigate(["/profile", this.user.id, section]);
    }
  }

  downloadDetails() {
    this.appService.showModal(UploadDetailsComponent, { size: "lg" });
  }

  logout() {
    this.appService.redirectToConnect(false, true);
  }

  signIn() {
    this.appService.redirectToConnect(true, false);
  }

  hamburgerClick() {
    this.appService.showNavOverlay = !this.appService.showNavOverlay;
  }

  trackPageEvent(tabName, navigationType: string = "main", navigationCore: string = "header") {
    this.avoService.avoInitted$
      .pipe(
        filter((result) => !!result),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const page = this.avoService.getPageProperties({ pageName: "Navigation" });
        const extra = {
          navigationType, // "main", "sub", "shop", or "image"
          navigationLabel: tabName,
          navigationCore, // e.g., "header", "footer", "sidebar"
          navigationGroup: null, // Optional - Label of header text above navigation item
          navigationFlyout: null, // Optional - Top level navigation value
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        this.avoService.trackEvent().navigationClicked(payload);
      });
  }
}
